import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const CHUNK_RELOAD_KEY = 'lazy-chunk-reload';
const CHUNK_RETRY_ATTEMPTS = 4;
const CHUNK_RETRY_DELAY_MS = 1500;
const CHUNK_RELOAD_WAIT_MS = 8000;

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('ChunkLoadError') ||
    message.includes('timeout')
  );
}

export function isChunkLoadErrorMessage(message: string): boolean {
  return isChunkLoadError(new Error(message));
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Clear after a full page load so a future deploy/HMR mismatch can auto-reload once. */
export function clearLazyChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // ignore private browsing / blocked storage
  }
}

async function loadModuleWithRetries<T extends ComponentType>(
  factory: () => Promise<{ default: T }>,
): Promise<{ default: T }> {
  let lastError: unknown;
  for (let attempt = 0; attempt < CHUNK_RETRY_ATTEMPTS; attempt += 1) {
    try {
      return await factory();
    } catch (error) {
      lastError = error;
      if (!isChunkLoadError(error) || attempt >= CHUNK_RETRY_ATTEMPTS - 1) {
        break;
      }
      await sleep(CHUNK_RETRY_DELAY_MS);
    }
  }
  throw lastError;
}

/** Install once — catches chunk errors CRA surfaces outside React.lazy promises. */
export function installLazyChunkRecovery(): void {
  const tryReloadOnce = (message: string): void => {
    if (!isChunkLoadErrorMessage(message)) return;
    let alreadyReloaded = false;
    try {
      alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
    } catch {
      alreadyReloaded = false;
    }
    if (alreadyReloaded) return;
    try {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    } catch {
      // ignore
    }
    window.location.reload();
  };

  window.addEventListener('error', (event) => {
    tryReloadOnce(event.message || '');
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason ?? '');
    tryReloadOnce(message);
  });
}

/**
 * CRA dev HMR often changes async chunk hashes while the tab keeps an old bundle.js.
 * Retries slow LAN chunk loads, then reloads once if still failing.
 */
export function lazyWithRetry<T extends ComponentType>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await loadModuleWithRetries(factory);
      clearLazyChunkReloadFlag();
      return module;
    } catch (error) {
      if (isChunkLoadError(error)) {
        let alreadyReloaded = false;
        try {
          alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
        } catch {
          alreadyReloaded = false;
        }
        if (!alreadyReloaded) {
          try {
            sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
          } catch {
            // ignore
          }
          window.location.reload();
          await sleep(CHUNK_RELOAD_WAIT_MS);
        }
      }
      throw error;
    }
  });
}

/** Email routes: bundle with parent chunk — avoids extra LAN chunk fetches in dev. */
export function lazyEmailRoute<T extends ComponentType>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazyWithRetry(() =>
    factory().then((module) => module),
  );
}
